/**
 * @fileoverview
 * Vuetify plugin configuration for the application.
 *
 * Sets up:
 *  - Material Design 3 blueprint defaults (md3)
 *  - Locale syncing with our LocaleManager
 *  - FontAwesome iconset (aliases + set) for Vuetify icon identifiers
 *  - NerdFonts CSS support for class-based icon rendering
 *  - Day.js adapter for date components
 *
 * The LocaleManager emits `locale-changed` events, which we
 * listen for in order to update Vuetify’s active locale at runtime.
 */

import { createVuetify } from 'vuetify';
import { md3 } from 'vuetify/blueprints';
import LocaleManager from '@/i12e/i18n/localeManager';
import { en, es } from 'vuetify/locale';
import DayJsAdapter from '@date-io/dayjs';
import { aliases, fa } from 'vuetify/iconsets/fa';
import 'vuetify/styles';

// Ensure FontAwesome CSS is available to <v-icon> components
import '@fortawesome/fontawesome-free/css/all.css';
// NerdFonts glyph classes are loaded globally in src/main.js. Components can
// prefer NerdFonts by passing classes such as `nf nf-fa-lock` to <v-icon>,
// while FontAwesome remains the configured fallback set for named icons.

/**
 * Instantiate Vuetify with our custom options.
 */
const vuetify = createVuetify({
  // Use the Material Design 3 blueprint for default styling
  blueprint: md3,

  locale: {
    // Initial locale from our LocaleManager
    locale: LocaleManager.activeLocale,
    // Fallback locale if a translation key is missing
    fallback: LocaleManager.fallbackLocale,
    // Built-in Vuetify translations (no need to duplicate)
    messages: {
      en, // English defaults
      es // Spanish defaults
    }
  },

  icons: {
    // Use FontAwesome (`fa`) as the default named icon set. Components may
    // still render NerdFonts via explicit classes on <v-icon>.
    defaultSet: 'fa',
    aliases, // Map component-friendly names → FA classes
    sets: {
      fa // The full FontAwesome icon set
    }
  },

  date: {
    // Plug in Day.js as the date adapter for Vuetify date components
    adapter: DayJsAdapter
  }
});

/**
 * When the application’s locale changes via LocaleManager,
 * update Vuetify’s locale in lock-step.
 */
LocaleManager.addEventListener('locale-changed', () => {
  vuetify.locale.current.value = LocaleManager.activeLocale;
});

export default vuetify;
